import { createStore, createEvent, createEffect, sample } from "effector";
import {
  PartnersState,
  PartnersApiResponse,
  PartnerFilters,
  PartnerDetailResponse,
  PartnersGeneralDataApiResponse,
} from "../model/types";
import { partnersService } from "../service/api";

export const fetchPartnersRequested = createEvent<PartnerFilters | undefined>();
export const loadMorePartnersRequested = createEvent<
  PartnerFilters | undefined
>();
export const fetchPartnerDetailRequested = createEvent<string>();
export const toggleMemberFollowRequested = createEvent<{
  memberId: string;
  isFollowing: boolean;
}>();
export const fetchPartnersGeneralDataRequested = createEvent();

export const fetchPartnersFx = createEffect(
  async (filters?: PartnerFilters): Promise<PartnersApiResponse> => {
    return await partnersService.getAllPartners(filters);
  },
);

export const loadMorePartnersFx = createEffect(
  async (filters?: PartnerFilters): Promise<PartnersApiResponse> => {
    return await partnersService.getAllPartners(filters);
  },
);

export const fetchPartnerDetailFx = createEffect(
  async (id: string): Promise<PartnerDetailResponse> => {
    return await partnersService.getPartnerById(id);
  },
);

export const toggleMemberFollowFx = createEffect(
  async ({
    memberId,
    isFollowing,
  }: {
    memberId: string;
    isFollowing: boolean;
  }): Promise<{ memberId: string; isFollowing: boolean }> => {
    await partnersService.toggleMemberFollow(memberId, isFollowing);
    return { memberId, isFollowing: !isFollowing };
  },
);

export const fetchPartnersGeneralDataRequestedFx = createEffect(
  async (): Promise<PartnersGeneralDataApiResponse> => {
    return await partnersService.getPartnersGeneralData();
  },
);

export const $partnersStore = createStore<PartnersState>({
  partners: [],
  selectedPartner: null,
  loading: false,
  loadMore: false,
  error: null,
  filters: {
    search: "",
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    total_count: 0,
  },
  partnersGeneralData: {
    total_organizations: 0,
    total_members: 0,
  },
});

$partnersStore
  .on(fetchPartnersRequested, (state) => ({
    ...state,
    loading: true,
    error: null,
  }))
  .on(fetchPartnersFx.doneData, (state, response) => ({
    ...state,
    partners: response.data,
    loading: false,
    error: null,
    pagination: {
      ...state.pagination,
      total: response.count,
      total_count: response.total_count,
      page: 1,
    },
  }))
  .on(fetchPartnersFx.failData, (state, error) => ({
    ...state,
    loading: false,
    error: error.message,
  }))
  .on(loadMorePartnersRequested, (state) => ({
    ...state,
    loadMore: true,
    error: null,
  }))
  .on(loadMorePartnersFx.doneData, (state, response) => ({
    ...state,
    partners: [...state.partners, ...response.data],
    loadMore: false,
    error: null,
    pagination: {
      ...state.pagination,
      page: state.pagination.page + 1,
      total: response.count,
      total_count: response.total_count,
    },
  }))
  .on(loadMorePartnersFx.failData, (state, error) => ({
    ...state,
    loading: false,
    error: error.message,
  }))
  .on(fetchPartnerDetailRequested, (state) => ({
    ...state,
    loading: true,
    error: null,
  }))
  .on(fetchPartnerDetailFx.doneData, (state, partner) => ({
    ...state,
    selectedPartner: partner,
    loading: false,
    error: null,
  }))
  .on(fetchPartnerDetailFx.failData, (state, error) => {
    // console.log(error);
    return {
      ...state,
      loading: false,
      error: error.message,
    };
  })
  .on(toggleMemberFollowFx.doneData, (state, { memberId, isFollowing }) => {
    if (!state.selectedPartner || !state.selectedPartner.members) {
      return state;
    }

    const updatedMembers = state.selectedPartner.members.map((member) => {
      if (member.id === memberId || member.username === memberId) {
        return {
          ...member,
          is_followed_by_me: isFollowing,
        };
      }
      return member;
    });

    return {
      ...state,
      selectedPartner: {
        ...state.selectedPartner,
        members: updatedMembers,
      },
    };
  })
  .on(toggleMemberFollowFx.failData, (state, error) => {
    // console.log("Follow action failed:", error);
    return {
      ...state,
      error: error.message,
    };
  })
  .on(fetchPartnersGeneralDataRequestedFx.doneData, (state, response) => {
    return {
      ...state,
      partnersGeneralData: response,
    };
  });

sample({
  clock: fetchPartnersRequested,
  target: fetchPartnersFx,
});

sample({
  clock: loadMorePartnersRequested,
  source: $partnersStore,
  fn: (state, filters) => ({
    ...filters,
    page: state.pagination.page + 1,
  }),
  target: loadMorePartnersFx,
});

sample({
  clock: fetchPartnerDetailRequested,
  target: fetchPartnerDetailFx,
});

sample({
  clock: toggleMemberFollowRequested,
  target: toggleMemberFollowFx,
});

sample({
  clock: fetchPartnersGeneralDataRequested,
  target: fetchPartnersGeneralDataRequestedFx,
});
